import { MakeLogicType, actions, connect, kea, key, listeners, path, props, reducers, selectors } from 'kea'
import { subscriptions } from 'kea-subscriptions'
import posthog from 'posthog-js'

import { logsConfigLogic } from 'products/logs/frontend/logsConfigLogic'
import { ParsedLogMessage } from 'products/logs/frontend/types'
import { getSessionIdFromLogAttributes } from 'products/logs/frontend/utils'

export type LogDetailsTab = 'details' | 'raw' | 'explore-ai' | 'comments' | 'related-errors'

export interface LogDetailsModalProps {
    id: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface logDetailsModalLogicValues {
    configuredSessionIdKeys: string[] | undefined // logsConfigLogic
    activeTab: LogDetailsTab
    hasSessionId: boolean
    isLogDetailsOpen: boolean
    jsonParseAllFields: boolean
    selectedLog: ParsedLogMessage | null
    sessionId: string | null
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface logDetailsModalLogicActions {
    closeLogDetails: () => {
        value: true
    }
    openLogDetails: (log: ParsedLogMessage) => {
        log: ParsedLogMessage
    }
    setActiveTab: (tab: LogDetailsTab) => {
        tab: LogDetailsTab
    }
    setJsonParseAllFields: (enabled: boolean) => {
        enabled: boolean
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface logDetailsModalLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        sessionId: (
            selectedLog: ParsedLogMessage | null,
            configuredSessionIdKeys: string[] | undefined
        ) => string | null
        hasSessionId: (sessionId: string | null) => boolean
    }
}

export type logDetailsModalLogicType = MakeLogicType<
    logDetailsModalLogicValues,
    logDetailsModalLogicActions,
    LogDetailsModalProps,
    logDetailsModalLogicMeta
>

export const logDetailsModalLogic = kea<logDetailsModalLogicType>([
    path(['products', 'logs', 'frontend', 'components', 'LogsViewer', 'LogDetailsModal', 'logDetailsModalLogic']),
    props({} as LogDetailsModalProps),
    key((props) => props.id),
    connect({ values: [logsConfigLogic, ['configuredSessionIdKeys']] }),

    actions({
        openLogDetails: (log: ParsedLogMessage) => ({ log }),
        closeLogDetails: true,
        setJsonParseAllFields: (enabled: boolean) => ({ enabled }),
        setActiveTab: (tab: LogDetailsTab) => ({ tab }),
    }),

    listeners(() => ({
        setActiveTab: ({ tab }) => {
            posthog.capture('logs details tab changed', { tab })
        },
    })),

    subscriptions(() => ({
        isLogDetailsOpen: (isLogDetailsOpen: boolean, wasOpen: boolean) => {
            if (isLogDetailsOpen && !wasOpen) {
                posthog.capture('logs details opened')
            }
        },
    })),

    reducers({
        selectedLog: [
            null as ParsedLogMessage | null,
            {
                openLogDetails: (_, { log }) => log,
                closeLogDetails: () => null,
            },
        ],
        isLogDetailsOpen: [
            false,
            {
                openLogDetails: () => true,
                closeLogDetails: () => false,
            },
        ],
        jsonParseAllFields: [
            false,
            {
                setJsonParseAllFields: (_, { enabled }) => enabled,
            },
        ],
        activeTab: [
            'details' as LogDetailsTab,
            {
                setActiveTab: (_, { tab }) => tab,
                openLogDetails: () => 'details',
                closeLogDetails: () => 'details',
            },
        ],
    }),

    selectors({
        sessionId: [
            (s) => [s.selectedLog, s.configuredSessionIdKeys],
            (selectedLog: ParsedLogMessage | null, configuredSessionIdKeys: string[] | undefined): string | null =>
                selectedLog
                    ? getSessionIdFromLogAttributes(
                          selectedLog.attributes,
                          selectedLog.resource_attributes,
                          configuredSessionIdKeys
                      )
                    : null,
        ],
        hasSessionId: [(s) => [s.sessionId], (sessionId: string | null): boolean => sessionId !== null],
    }),
])
